import React from 'react';
import { CheckCircle2, XCircle, Brain } from 'lucide-react';
import CTASection from '../../components/CTASection';
import ChaosToOrder from '../../components/ChaosToOrder';
import SEO from '../../components/SEO';
import GradientOverlay from '../../components/GradientOverlay';
import LogomarkPattern from '../../components/LogomarkPattern';

const SmartCRM = () => {
  const useCases = [
    {
      title: 'Financial Services Provider',
      location: 'Singapore',
      description: 'Uses AI to analyse client communication and predict which clients are at risk of churning, allowing for proactive intervention before it\'s too late.'
    },
    {
      title: 'Commercial Real Estate Agency',
      location: 'Gold Coast',
      description: 'Uses Smart CRM to identify patterns in property searches and client inquiries to forecast demand for specific types of commercial space.'
    },
    {
      title: 'B2B Equipment Supplier',
      location: 'Brisbane',
      description: 'Gets automated recommendations on which existing clients are most likely to need servicing or equipment upgrades based on usage data and purchase history.'
    }
  ];

  const costsOfInaction = [
    'Making critical decisions based on gut feel alone',
    'Missing valuable upsell and cross-sell opportunities',
    'Being blindsided by customer churn',
    'Failing to spot emerging market trends before competitors',
    'Wasting time manually analysing data that AI could process instantly'
  ];

  return (
    <div style={{ marginTop: '80px' }}>
      <SEO 
        title="Smart CRM & Analytics Gold Coast"
        description="Advanced CRM analytics and AI-powered insights for Gold Coast businesses. Transform your customer data into actionable intelligence."
        keywords="Smart CRM Gold Coast, CRM Analytics Australia, AI CRM Brisbane, Predictive Analytics Queensland, Customer Intelligence Gold Coast, CRM Reporting Australia"
        canonical="https://resolvdigital.com.au/services/smart-crm"
      />
      
      {/* Hero Section - DARK with Background Image */}
      <section style={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#0a4447',
        backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        padding: '120px 40px 80px'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(10, 68, 71, 0.92)'
        }} />
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{ maxWidth: '900px' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              backgroundColor: 'rgba(13, 115, 119, 0.2)',
              border: '1px solid #0D7377',
              borderRadius: '10rem',
              padding: '8px 20px',
              marginBottom: '24px'
            }}>
              <Brain size={20} style={{ color: '#0D7377' }} />
              <span style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#0D7377',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Service
              </span>
            </div>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 900,
              fontSize: 'clamp(3rem, 6vw, 5rem)',
              lineHeight: 1.1,
              color: '#C9A227',
              textTransform: 'uppercase',
              marginBottom: '32px'
            }}>
              Resolve Market Guesswork.<br />Make Data-Driven Decisions.
            </h1>
            <p style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '1.5rem',
              lineHeight: 1.6,
              color: '#ffffff'
            }}>
              Augment your CRM with AI to automatically analyse data, identify trends, predict customer behaviour, and surface opportunities or risks that would otherwise be hidden.
            </p>
          </div>
        </div>
      </section>

      {/* What is Smart CRM - LIGHT TEAL with Chaos Animation */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.3} />
        </div>
        
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 244, 245, 0.4) 0%, rgba(255, 255, 255, 0.5) 50%, rgba(232, 244, 245, 0.3) 100%)',
          zIndex: 1,
          pointerEvents: 'none'
        }} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
            gap: '40px',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textTransform: 'uppercase',
                marginBottom: '32px'
              }}>
                What is a Smart CRM?
              </h2>
              <div style={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: '#0a4447'
              }}>
                <p style={{ marginBottom: '20px' }}>
                  A Smart CRM uses artificial intelligence to turn your customer data into actionable intelligence. It doesn't just store information—it learns from it.
                </p>
                <p style={{ marginBottom: '20px' }}>
                  It can predict which customers are likely to churn, which deals are most likely to close, what products a customer might need next, and when is the best time to reach out.
                </p>
                <p style={{ fontWeight: 600, color: '#C9A227' }}>
                  It's like having a data analyst working 24/7, finding insights you'd never spot manually.
                </p>
              </div>
            </div>
            <div style={{
              backgroundColor: '#ffffff',
              border: '2px solid #0D7377',
              borderRadius: '8px',
              padding: '48px',
              boxShadow: '0 8px 32px rgba(13, 115, 119, 0.1)'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 700,
                fontSize: '1.5rem',
                color: '#0a4447',
                marginBottom: '24px',
                textTransform: 'uppercase'
              }}>
                AI-Powered Capabilities
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {[
                  'Churn prediction & prevention',
                  'Next-best-action recommendations',
                  'Sentiment analysis from communications',
                  'Deal likelihood scoring',
                  'Automated market trend detection'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '1rem',
                    color: '#0a4447'
                  }}>
                    <CheckCircle2 size={24} style={{
                      color: '#0D7377',
                      flexShrink: 0,
                      marginTop: '2px'
                    }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why It's Relevant - WHITE with Logomark Pattern */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.025} density="medium" tint="teal" />
        <GradientOverlay variant="subtle" opacity={0.1} />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '32px'
          }}>
            Why It's a Game-Changer for SMEs
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.7,
            color: '#0a4447',
            maxWidth: '900px',
            margin: '0 auto 48px'
          }}>
            AI makes enterprise-level analytics accessible to SMEs. You can now leverage your own data to understand your market deeply, anticipate customer needs, and proactively engage with them—capabilities that were once only available to corporations with dedicated data science teams.
          </p>
        </div>
      </section>

      {/* Cost of Inaction - LIGHT TEAL */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#e8f4f5',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LogomarkPattern opacity={0.02} density="sparse" tint="gold" />
        
        <div className="container" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            background: 'linear-gradient(135deg, #0a4447 0%, #C9A227 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            The Cost of Manual Analysis
          </h2>
          <div style={{
            backgroundColor: '#ffffff',
            border: '2px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '8px',
            padding: '48px',
            maxWidth: '900px',
            margin: '0 auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }}>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {costsOfInaction.map((cost, index) => (
                <li key={index} style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.6,
                  color: '#1a1c1b'
                }}>
                  <XCircle size={24} style={{
                    color: '#dc3545',
                    flexShrink: 0,
                    marginTop: '4px'
                  }} />
                  <span>{cost}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Use Cases - DARK (contrast section) */}
      <section style={{
        padding: '120px 40px',
        backgroundColor: '#084144',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <ChaosToOrder showLabels={false} opacity={0.1} />
        </div>
        <div className="container" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 2
        }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 1.1,
            color: '#C9A227',
            textTransform: 'uppercase',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Real Australian B2B Success Stories
          </h2>
          <p style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#b8d8da',
            maxWidth: '800px',
            margin: '0 auto 80px',
            textAlign: 'center'
          }}>
            See how AI-powered intelligence is transforming decision-making for Australian businesses.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 350px), 1fr))',
            gap: '40px'
          }}>
            {useCases.map((useCase, index) => (
              <div key={index} style={{
                backgroundColor: 'rgba(10, 93, 96, 0.6)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(201, 162, 39, 0.3)',
                borderRadius: '8px',
                padding: '40px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#C9A227';
                e.currentTarget.style.transform = 'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(201, 162, 39, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                <div style={{
                  display: 'inline-block',
                  backgroundColor: '#0D7377',
                  color: '#ffffff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {useCase.location}
                </div>
                <h3 style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#C9A227',
                  marginBottom: '16px'
                }}>
                  {useCase.title}
                </h3>
                <p style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  lineHeight: 1.7,
                  color: '#ffffff'
                }}>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection />
    </div>
  );
};

export default SmartCRM;
